import React, { useState } from "react";
import { CheckCircle2, Sparkles, Clock, AlertTriangle, Phone, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RISK_STYLES } from "@/lib/appUtils";
import { useBranding } from "@/lib/branding";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TIER_TO_STYLE = { Routine: "low", "Needs Attention": "moderate", "High-Risk": "high" };

function TierIcon({ tier, className }) {
  if (tier === "Routine") return <CheckCircle2 className={cn("w-4 h-4", className)} />;
  if (tier === "Needs Attention") return <Phone className={cn("w-4 h-4", className)} />;
  return <AlertTriangle className={cn("w-4 h-4", className)} />;
}

export function WeeklyStatusCard({ summary, onApproved }) {
  const { hospCode } = useBranding();
  const style = RISK_STYLES[TIER_TO_STYLE[summary.tier]];
  const isRoutine = summary.tier === "Routine";
  const [approving, setApproving] = useState(false);
  const [approvedAt, setApprovedAt] = useState(summary.approved_at);

  const approve = async () => {
    setApproving(true);
    try {
      const res = await api.approveWeek(summary.patient_id, summary.week);
      setApprovedAt(res.approved_at);
      toast.success(`Week ${summary.week} approved for ${summary.patient_name.split(" ")[0]}`);
      onApproved && onApproved(summary.patient_id, summary.week);
    } catch {
      toast.error("Approval failed");
    } finally { setApproving(false); }
  };

  return (
    <div
      data-testid={`weekly-card-${summary.patient_id}`}
      className={cn(
        "surface-card p-5 shadow-sm hover:shadow-md transition-all flex flex-col gap-3",
        summary.tier === "High-Risk" && "border-rose-300/60 dark:border-rose-800"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn(
              "inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border",
              style.badge
            )}>
              <TierIcon tier={summary.tier} className="w-3 h-3" />
              {summary.tier}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
              Week {summary.week} · {summary.day_range}
            </span>
          </div>
          <div className="font-semibold text-foreground mt-1.5 truncate">{summary.patient_name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {summary.surgery_type} · Day {summary.current_day}/{summary.total_days}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-bold tabular-nums text-foreground leading-none">{summary.risk_score}</div>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-medium mt-1">risk</div>
        </div>
      </div>

      <div className="text-sm text-foreground/90 leading-relaxed line-clamp-4">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mr-1">
          <Sparkles className="w-3 h-3 brand-text" /> AI summary
        </span>
        {summary.summary}
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-border">
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {summary.progress.done}/{summary.progress.total} items done
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" className="rounded-full h-8 text-xs"
            data-testid={`weekly-open-${summary.patient_id}`}>
            <Link to={`/${hospCode}/doctor/patient/${summary.patient_id}`}>
              Open <ArrowUpRight className="w-3 h-3 ml-1" />
            </Link>
          </Button>
          {approvedAt ? (
            <span
              data-testid={`weekly-approved-${summary.patient_id}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 px-2 py-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Approved
            </span>
          ) : isRoutine ? (
            <Button
              size="sm" className="rounded-full h-8 text-xs"
              onClick={approve} disabled={approving}
              data-testid={`weekly-approve-${summary.patient_id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Glance & approve
            </Button>
          ) : (
            <Button
              variant="outline" size="sm" className="rounded-full h-8 text-xs"
              onClick={approve} disabled={approving}
              data-testid={`weekly-approve-${summary.patient_id}`}
            >
              Acknowledge
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
